"use client";

import React from "react";
import { Activity, Clock, Phone, ShieldCheck, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const credentials = [{
  icon: Clock,
  label: "60-Min Target On-Site",
  detail: "Rapid regional dispatch"
}, {
  icon: ShieldCheck,
  label: "Direct Insurance Billing",
  detail: "Adjuster claim coordination"
}, {
  icon: Activity,
  label: "Dedicated Local Crews",
  detail: "Zero third-party brokers"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="dark" className="relative min-h-[90vh] flex flex-col justify-between overflow-hidden bg-background">
      {/* Background Image Container with Dark Scrim Overlay */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_field_crew.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_field_crew.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_emergency_field_crew.png-wvc-sizes" alt="TriServe restoration technicians deploying commercial water extraction and drying equipment on site" loading="eager" decoding="async" className="w-full h-full object-cover object-center scale-100" />
        {/* Dark Industrial Scrim */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/80 to-foreground/95" aria-hidden="true" />
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 md:pt-36 md:pb-20 lg:pt-40 flex-1 flex flex-col justify-center">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-4xl">
          {/* Operational Status Pill */}
          <motion.div variants={itemVariants} className="mb-6 inline-flex">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-primary/40 bg-foreground/50 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-background shadow-xs backdrop-blur-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <span>24/7 DISPATCH READY</span>
              <span className="text-muted-foreground/60">•</span>
              <span className="font-mono text-muted">MD • DC • NOVA</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-background leading-[1.1] mb-5 font-default">
            24/7 Emergency restoration for Maryland, DC, and Northern Virginia
          </motion.h1>

          {/* Subheading / Dictated Lead */}
          <motion.p variants={itemVariants} className="text-lg sm:text-xl md:text-2xl font-semibold text-accent mb-4 tracking-tight">
            Local crews, no call centers.
          </motion.p>

          {/* Explanatory Body Paragraph */}
          <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted max-w-2xl leading-relaxed mb-8 sm:mb-10 font-default">
            When flood water, fire smoke, or mold compromises your property, TriServe dispatches our own certified field teams straight to your door. We manage rapid water extraction, structural drying, and handle all documentation with your insurance adjuster.
          </motion.p>

          {/* Call to Action Triggers */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {/* Primary Call Action */}
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-bold px-7 h-12 text-base shadow-sm transition-[color,background-color,border-color,box-shadow]">
              <a href="tel:8554356631" className="inline-flex items-center justify-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call 855-435-6631</span>
              </a>
            </Button>

            {/* Safety Yellow Intake Action */}
            <Button asChild size="lg" className="bg-accent text-accent-foreground border border-accent/80 hover:bg-accent/90 rounded-md font-extrabold px-7 h-12 text-base shadow-md transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Technical Spec & Operational Telemetry Bar */}
      <div className="relative z-10 border-t border-border/20 bg-foreground/60 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8">
            {credentials.map((item, i) => {
            const IconComp = item.icon;
            return <div key={item.label} data-index={i} className="flex items-center gap-3.5 text-background">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-primary/40 bg-primary/20 text-accent">
                    <IconComp className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-background leading-tight">
                      {item.label}
                    </div>
                    <div className="text-xs text-muted-foreground/90 font-mono tracking-tight mt-0.5">
                      {item.detail}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}